import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useLanguage } from '../LanguageContext';
import { replayUsers, replaySessions, replaySessionEvents } from '../api';

// Hidden admin-only session-replay viewer (Yamam only). Not in any nav — reached by
// direct URL (/__replay). Three columns: team members (per user) → that user's sessions
// → playback with rrweb-player.
export default function SessionReplay() {
  const { lang, isRTL } = useLanguage();
  const ar = lang === 'ar';
  const t = (a, e) => (ar ? a : e);
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [activeUser, setActiveUser] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [activeSession, setActiveSession] = useState(null);
  const [status, setStatus] = useState('');
  const playerRef = useRef(null);
  const playerInst = useRef(null);

  const loadUsers = useCallback(async () => {
    setLoadingUsers(true);
    const r = await replayUsers();
    setUsers(r.users || []);
    setLoadingUsers(false);
  }, []);
  useEffect(() => { loadUsers(); }, [loadUsers]);

  const pickUser = async (u) => {
    setActiveUser(u); setSessions([]); setActiveSession(null); setStatus('');
    if (playerRef.current) playerRef.current.innerHTML = '';
    if (Number(u.sessions) === 0) return;
    setLoadingSessions(true);
    const r = await replaySessions({ userId: u.id, limit: 200 });
    setSessions(r.sessions || []);
    setLoadingSessions(false);
  };

  const play = async (s) => {
    setActiveSession(s.session_id);
    setStatus(t('جارٍ التحميل…', 'Loading…'));
    const r = await replaySessionEvents(s.session_id);
    const raw = (r.events || []).slice().sort((a, b) => a.timestamp - b.timestamp);
    if (raw.length < 2) { setStatus(t('لا توجد لقطات كافية لعرض هذه الجلسة.', 'Not enough frames to replay this session.')); return; }
    // Collapse idle gaps so the player timeline reflects real activity, not a tab left
    // open for hours. Any gap > 10min is squeezed to 2s by shifting later timestamps.
    const IDLE = 10 * 60 * 1000, KEEP = 2000;
    let shift = 0;
    const events = raw.map((e, i) => {
      if (i > 0) { const gap = raw[i].timestamp - raw[i - 1].timestamp; if (gap > IDLE) shift += gap - KEEP; }
      return { ...e, timestamp: e.timestamp - shift };
    });
    try {
      const mod = await import('rrweb-player');
      await import('rrweb-player/dist/style.css');
      const Player = mod.default || mod;
      if (playerInst.current) { try { playerInst.current.$destroy && playerInst.current.$destroy(); } catch (e) {} playerInst.current = null; }
      if (playerRef.current) playerRef.current.innerHTML = '';
      const w = Math.min(1000, (playerRef.current?.clientWidth || 880));
      playerInst.current = new Player({
        target: playerRef.current,
        props: { events, width: w, height: Math.round(w * 0.62), autoPlay: true, showController: true, skipInactive: true, speedOption: [1, 2, 4, 8] },
      });
      setStatus('');
    } catch (e) { setStatus(t('تعذّر تشغيل المشغّل.', 'Could not load the player.')); }
  };

  const fmtDur = (s) => { s = Number(s) || 0; const m = Math.floor(s / 60); return m >= 60 ? `${Math.floor(m / 60)}${t('س','h')} ${m % 60}${t('د','m')}` : m >= 1 ? `${m}${t('د','m')} ${s % 60}${t('ث','s')}` : `${s}${t('ث','s')}`; };
  const fmtT = (d) => { if (!d) return '—'; try { return new Date(d).toLocaleString(ar ? 'ar-SA' : 'en-GB'); } catch { return '—'; } };
  const col = { border: '1px solid var(--border)', borderRadius: 12, maxHeight: '72vh', overflowY: 'auto' };

  return (
    <div dir={isRTL ? 'rtl' : 'ltr'} style={{ maxWidth: 1200, margin: '0 auto', padding: '8px 4px 60px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
        <span style={{ fontSize: '1.2rem' }}>🎬</span>
        <h2 style={{ fontSize: '1.2rem', fontWeight: 800, margin: 0 }}>{t('تسجيلات جلسات الفريق', 'Team session recordings')}</h2>
        <button onClick={loadUsers} style={{ marginInlineStart: 'auto', padding: '7px 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', cursor: 'pointer', fontSize: '0.82rem' }}>↻ {t('تحديث', 'Refresh')}</button>
      </div>

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        {/* 1) Users (per user) */}
        <div style={{ ...col, flex: '1 1 240px', minWidth: 220 }}>
          <div style={{ padding: '8px 12px', fontSize: '0.7rem', fontWeight: 800, textTransform: 'uppercase', color: 'var(--text-secondary)', borderBottom: '1px solid var(--border)' }}>{t('الموظفون', 'Team members')}</div>
          {loadingUsers ? <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-secondary)' }}>…</div>
          : users.length === 0 ? <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-secondary)', fontSize: '0.84rem' }}>{t('لا يوجد موظفون', 'No members')}</div>
          : users.map((u) => (
            <div key={u.id} onClick={() => pickUser(u)}
              style={{ padding: '10px 12px', borderBottom: '1px solid var(--border)', cursor: 'pointer',
                background: activeUser?.id === u.id ? 'var(--accent)' : 'transparent', color: activeUser?.id === u.id ? '#fff' : 'var(--text-primary)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center' }}>
                <span style={{ fontWeight: 700, fontSize: '0.88rem' }}>{u.name}</span>
                <span style={{ fontSize: '0.7rem', fontWeight: 800, padding: '1px 8px', borderRadius: 10, background: activeUser?.id === u.id ? 'rgba(255,255,255,0.25)' : (Number(u.sessions) ? 'var(--accent)' : 'var(--border)'), color: activeUser?.id === u.id ? '#fff' : (Number(u.sessions) ? '#fff' : 'var(--text-secondary)') }}>{u.sessions}</span>
              </div>
              <div style={{ fontSize: '0.68rem', opacity: 0.8, marginTop: 2 }}>{u.role}{u.last_recorded ? ` · ${t('آخر تسجيل','last')} ${fmtT(u.last_recorded)}` : ` · ${t('لا تسجيلات','no recordings')}`}</div>
            </div>
          ))}
        </div>

        {/* 2) Sessions for the selected user */}
        <div style={{ ...col, flex: '1 1 280px', minWidth: 240 }}>
          <div style={{ padding: '8px 12px', fontSize: '0.7rem', fontWeight: 800, textTransform: 'uppercase', color: 'var(--text-secondary)', borderBottom: '1px solid var(--border)' }}>{t('الجلسات', 'Sessions')}{activeUser ? ` — ${activeUser.name}` : ''}</div>
          {!activeUser ? <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-secondary)', fontSize: '0.84rem' }}>{t('اختر موظفًا', 'Pick a member')}</div>
          : loadingSessions ? <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-secondary)' }}>…</div>
          : sessions.length === 0 ? <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-secondary)', fontSize: '0.84rem' }}>{t('لا توجد تسجيلات لهذا الموظف بعد.', 'No recordings for this member yet.')}</div>
          : sessions.map((s) => (
            <div key={s.session_id} onClick={() => play(s)}
              style={{ padding: '10px 12px', borderBottom: '1px solid var(--border)', cursor: 'pointer',
                background: activeSession === s.session_id ? 'var(--accent)' : 'transparent', color: activeSession === s.session_id ? '#fff' : 'var(--text-primary)' }}>
              <div style={{ fontSize: '0.84rem', fontWeight: 700 }}>{fmtT(s.started_at)}</div>
              <div style={{ fontSize: '0.7rem', opacity: 0.85, marginTop: 2 }}>{t('نشاط', 'active')} {fmtDur(s.duration_s)} · {s.event_count} {t('لقطة', 'frames')}</div>
            </div>
          ))}
        </div>

        {/* 3) Player */}
        <div style={{ flex: '2 1 520px', minWidth: 320 }}>
          {status && <div style={{ padding: 12, color: 'var(--text-secondary)', fontSize: '0.86rem' }}>{status}</div>}
          <div ref={playerRef} style={{ minHeight: 200 }} />
          {!activeSession && !status && <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)', fontSize: '0.86rem' }}>{t('اختر جلسة لمشاهدتها.', 'Pick a session to replay it.')}</div>}
        </div>
      </div>
    </div>
  );
}
